'use client';

import { useOnType } from '@/globals/helpers/useOnType';
import { submitButtonBox } from '@/uiComposites/interactive/callToAction';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { icon } from '@/uiPrimitives/media/icon';
import { textBlock } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@/globals/testHelpers/testSelector';
import { useSearchParams } from 'next/navigation';
import { ElementRef, useId, useRef } from 'react';
import { pageGridItem } from '@/uiPrimitives/layout/pageGrid/pageGridItem';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import { translateInline } from '@/globals/translate/translateInline';

export const GlossarySearch = () => {
	const router = useDOPRouter();
	const id = useId();
	const inputRef = useRef<ElementRef<'input'>>(null);

	const searchParams = useSearchParams().toString();

	const handleSearch = (value: string) => {
		const urlsearch = new URLSearchParams(searchParams);

		if (value === '') {
			urlsearch.delete('term');
		} else {
			urlsearch.set('term', value);
		}

		router.replace(`?${urlsearch.toString()}`, { scroll: false });
	};

	const { formProps, inputProps } = useOnType(
		{
			onBlur: (value) => {
				handleSearch(value);
			},
			onChange: (value) => {
				handleSearch(value);
			},
			action: (formData: FormData) => {
				const term = formData.get('search');

				if (typeof term !== 'string') return;
				handleSearch(term);
				inputRef.current?.blur?.();
			},
		},
		400,
	);

	const searchState = useSearchParams().get('term') ?? '';

	return (
		<form
			{...formProps}
			className={classes(
				shelf({ gridTemplateColumns: 'minmax(0, 1fr) auto' }),
				zIndex({ isolation: 'isolate' }),
				pageGridItem({ gridColumn: 'start-8-of-12' }),
			)}
		>
			<input
				{...inputProps}
				ref={inputRef}
				type="search"
				name="search"
				autoCapitalize="none"
				autoComplete="off"
				id={id}
				defaultValue={searchState}
				className={classes(
					textBlock({}),
					box({
						paddingBlock: '3 | h1',
						paddingInline: indentSize,
						backgroundColor: 'negative',
						borderWidth: '1px',
						borderStyle: 'solid',
						borderColor: 'positive',
					}),
					zIndex({
						'zIndex:focusVisible': '1',
					}),
				)}
				{...addTestSelector('uiSearchInput')}
			/>
			<button
				type="submit"
				aria-label={translateInline({
					en: 'Search',
					nl: 'Zoeken',
				})}
				className={classes(submitButtonBox({}))}
				{...addTestSelector('uiSearchSubmitBtn')}
			>
				<span
					className={classes(
						occupy({ inlineSize: 'capSize', blockSize: 'capSize' }),
					)}
				>
					<i
						className={classes(icon({ icon: searchIcon, blockSize: '2 | h2' }))}
					/>
				</span>
			</button>
		</form>
	);
};
